import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMachine } from '@xstate/react';
import type { InspectionEvent } from 'xstate';
import { getVoiceLiveInspect } from '../machine/inspector';
import { voiceLiveMachine } from '../machine/voiceLiveMachine';
import { useMachineEventTelemetry } from '../inspector/telemetry/useMachineEventTelemetry';
import type { AvatarState, ConnectionState, TokenUsageSummary } from '../types';
import { selectVoiceLiveDisplayState } from '../utils';
import { useAvatarPeerConnection } from './useAvatarPeerConnection';
import { useBeforeUnload } from './useBeforeUnload';
import { useConversationTranscript } from './useConversationTranscript';
import { useDevFallbackToggle } from './useDevFallbackToggle';
import { useNetworkStatus } from './useNetworkStatus';
import { useTokenUsage } from './useTokenUsage';
import { useVoiceLiveAudio } from './useVoiceLiveAudio';
import { useVoiceLiveTelemetry } from './useVoiceLiveTelemetry';
import { detachWebSocketHandlers, openVoiceLiveSocket, type MachineDeps } from './voiceLiveSocket';

export interface UseVoiceLiveSessionOptions {
  /** Authenticated user id. */
  userId: string;
  /** Scenario id for the roleplay. */
  scenarioId: string;
  /** Whether video is enabled when the session starts. */
  defaultVideoEnabled: boolean;
  /** Called when the FieldOne session id is created. */
  onSessionCreated?: (sessionId: number) => void;
}

export interface UseVoiceLiveSessionResult {
  connectionState: ConnectionState;
  isActive: boolean;
  retryCount: number;
  handleStart: () => void;
  handleStop: () => void;
  handleManualRetry: () => void;
  avatarState: AvatarState;
  videoRef: MutableRefObject<HTMLVideoElement | null>;
  isDegraded: boolean;
  isOffline: boolean;
  isVideoEnabled: boolean;
  handleModeChange: (videoSelected: boolean) => void;
  showAudioVisualizer: boolean;
  showConnectionOverlay: boolean;
  errorMessage: string;
  inputAnalyser: AnalyserNode | null;
  outputAnalyser: AnalyserNode | null;
  devToolsVisible: boolean;
  forceAudioFallback: boolean;
  setForceAudioFallback: Dispatch<SetStateAction<boolean>>;
  forceIceTimeout: boolean;
  setForceIceTimeout: Dispatch<SetStateAction<boolean>>;
  showTokenUsage: boolean;
  setShowTokenUsage: Dispatch<SetStateAction<boolean>>;
  tokenUsageSummary: TokenUsageSummary | null;
}

/**
 * Composition root for the Voice Live training session, backed by the XState
 * `voiceLiveMachine`. The machine owns the connection + fallback *decisions*
 * (pure logic, unit-tested in isolation); this hook owns the *effects*: it
 * instantiates the audio / peer / telemetry / transcript subsystems and
 * `.provide()`s the machine's effect actions with React-backed implementations.
 *
 * The WebSocket stays imperative — `createWebSocket` builds the socket, stores
 * it in the shared `wsRef` (so the peer hook can still hijack `onmessage` for
 * the SDP answer), and translates frames into semantic machine events via
 * `self.send`. The peer hook is used unchanged: its failure/active signals are
 * bridged to `AVATAR_FAILED` / `AVATAR_ACTIVE` events.
 */
export const useVoiceLiveSession = ({
  userId,
  scenarioId,
  defaultVideoEnabled,
  onSessionCreated,
}: UseVoiceLiveSessionOptions): UseVoiceLiveSessionResult => {
  // Shared, imperative WebSocket (the peer hook reads/hijacks this).
  const wsRef = useRef<WebSocket | null>(null);

  // Mirror refs (read-mostly) synced FROM machine context each render so the
  // peer hook sees the latest values without its own state.
  const hasIceServersRef = useRef<boolean>(false);
  const isVideoEnabledRef = useRef<boolean>(defaultVideoEnabled);

  const onSessionCreatedRef = useRef(onSessionCreated);
  onSessionCreatedRef.current = onSessionCreated;

  const depsRef = useRef<MachineDeps | null>(null);

  // Subsystems (instantiated below the machine where they need its snapshot).
  const telemetry = useVoiceLiveTelemetry({ scenarioId });
  const audio = useVoiceLiveAudio();
  const transcript = useConversationTranscript();
  const tokenUsage = useTokenUsage();
  const machineTelemetry = useMachineEventTelemetry(telemetry);
  const machineTelemetryRef = useRef(machineTelemetry);
  machineTelemetryRef.current = machineTelemetry;
  const {
    forceAudioFallback,
    setForceAudioFallback,
    devToolsVisible,
    forceAudioFallbackRef,
    forceIceTimeout,
    setForceIceTimeout,
    forceIceTimeoutRef,
    showTokenUsage,
    setShowTokenUsage,
  } = useDevFallbackToggle();

  // Provide the machine's effect actions once. The closures read the stable
  // refs above (depsRef/wsRef/...) at action runtime, by which point the
  // subsystems are wired.
  const [machine] = useState(() =>
    voiceLiveMachine.provide({
      actions: {
        createWebSocket: ({ context, self }) => {
          const deps = depsRef.current;
          if (!deps) return;
          openVoiceLiveSocket({
            context,
            deps,
            wsRef,
            send: (event) => self.send(event),
            getContext: () => self.getSnapshot().context,
            onSessionCreated: onSessionCreatedRef.current,
          });
        },

        teardown: () => {
          const deps = depsRef.current;
          if (!deps) return;
          const { audio: a, transcript: tr, peer } = deps;
          a.stopPlayback();
          a.teardownAnalysers();
          a.stopCapture();
          const ws = wsRef.current;
          if (ws?.readyState === WebSocket.OPEN && tr.hasTurns()) {
            ws.send(JSON.stringify(tr.buildFinalizePayload()));
          }
          if (ws) {
            detachWebSocketHandlers(ws);
            wsRef.current = null;
            ws.close(4000, 'User initiated disconnect');
          }
          a.closeContext();
          peer.cleanupAvatar();
          tr.reset();
          deps.tokenUsage.reset();
        },

        forceCloseSocket: () => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.close();
          }
        },

        detachAvatarVideo: () => {
          depsRef.current?.peer.detachVideo();
        },

        handleIceServerTimeout: ({ context, self }) => {
          const autoStart = !context.socketIsAudioOnly && context.isVideoEnabled;
          if (!autoStart || context.hasIceServers) return;
          depsRef.current?.telemetry.trackDiagnostic('Timer', 'IceServerTimeout', {
            userId: context.userId,
          });
          self.send({ type: 'AVATAR_FAILED', reason: 'ice-server-timeout' });
        },

        retryAvatarPeer: ({ context, self }) => {
          const peer = depsRef.current?.peer;
          if (!peer) return;
          // Tear down the failed peer first so startAvatar isn't a no-op.
          peer.cleanupAvatar();
          if (context.socketIsAudioOnly || wsRef.current?.readyState !== WebSocket.OPEN) {
            return;
          }
          if (context.iceServers.length === 0) {
            self.send({ type: 'AVATAR_FAILED', reason: 'no-ice-servers-on-retry' });
            return;
          }
          peer.startAvatar(context.iceServers);
        },

        maybeAutoStartAvatar: ({ context }) => {
          const peer = depsRef.current?.peer;
          if (!peer) return;
          const autoStart = !context.socketIsAudioOnly && context.isVideoEnabled;
          if (
            autoStart &&
            context.sessionReady &&
            context.hasIceServers &&
            peer.avatarStateRef.current === 'inactive'
          ) {
            peer.startAvatar(context.iceServers);
          }
        },
      },
    }),
  );

  // The dev inspector stays opt-in; App Insights always gets the stream.
  const [inspect] = useState(() => {
    const devInspect = getVoiceLiveInspect();
    return (inspectionEvent: InspectionEvent) => {
      devInspect?.next?.(inspectionEvent);
      machineTelemetryRef.current.inspect(inspectionEvent);
    };
  });

  const [snapshot, send] = useMachine(machine, {
    input: { userId, scenarioId, defaultVideoEnabled },
    inspect,
  });

  // WebRTC avatar peer — it reports lifecycle signals; the machine decides what they mean.
  const peer = useAvatarPeerConnection({
    userId,
    telemetry,
    wsRef,
    avatarIceServers: snapshot.context.iceServers,
    hasIceServersRef,
    isVideoEnabledRef,
    forceFailRef: forceAudioFallbackRef,
    forceIceTimeoutRef,
    tapOutputFromStream: audio.tapOutputFromStream,
    setErrorMessage: (message) => send({ type: 'SET_ERROR', message }),
    onAvatarActive: () => send({ type: 'AVATAR_ACTIVE' }),
    onAvatarFailure: (reason) => send({ type: 'AVATAR_FAILED', reason }),
  });

  // Wire the subsystem deps and mirror refs for the machine's effect actions.
  depsRef.current = { telemetry, audio, transcript, tokenUsage, peer };
  hasIceServersRef.current = snapshot.context.hasIceServers;
  isVideoEnabledRef.current = snapshot.context.isVideoEnabled;

  // Browser online/offline → machine events (the machine decides what to do).
  useNetworkStatus({
    onOffline: () => send({ type: 'OFFLINE' }),
    onOnline: () => send({ type: 'ONLINE' }),
  });

  // Seed connectivity once on mount: useNetworkStatus only fires on change, so a
  // cold offline load would otherwise look online.
  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      send({ type: 'OFFLINE' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Finalize + close the socket cleanly on page unload.
  useBeforeUnload(() => {
    // No machine event fires when the user simply leaves, so the stream needs a last word.
    telemetry.trackDiagnostic('Browser', 'PageHide', {
      state: JSON.stringify(snapshot.value),
      userId,
    });
    telemetry.flushNow();
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      if (transcript.hasTurns()) {
        ws.send(JSON.stringify(transcript.buildFinalizePayload()));
      }
      detachWebSocketHandlers(ws);
      wsRef.current = null;
      ws.close(4000, 'Page unloading');
    }
  });

  // Teardown on unmount (the machine actor stops, but its effects must run).
  useEffect(() => {
    return () => {
      audio.stopPlayback();
      audio.teardownAnalysers();
      audio.stopCapture();
      if (wsRef.current) {
        const ws = wsRef.current;
        detachWebSocketHandlers(ws);
        wsRef.current = null;
        ws.close(4000, 'Component unmounted');
      }
      audio.closeContext();
      peer.cleanupAvatar();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStart = () => {
    send({ type: 'START' });
  };

  const handleStop = () => {
    send({ type: 'STOP' });
  };

  const handleManualRetry = () => {
    send({ type: 'MANUAL_RETRY' });
  };

  /**
   * Turn video back on over a socket that was already negotiated for video:
   * re-attach the stored stream, or rebuild the peer when the live one has no
   * video transceiver.
   */
  const enableVideoInPlace = () => {
    send({ type: 'SET_VIDEO_ENABLED', enabled: true });
    if (
      !peer.attachStoredVideo() &&
      !peer.hasVideoTransceiver() &&
      snapshot.context.iceServers.length > 0
    ) {
      peer.cleanupAvatar();
      peer.startAvatar(snapshot.context.iceServers);
    }
  };

  const isDegraded = snapshot.context.degradedToAudio;
  const isOffline = snapshot.matches({ network: 'offline' });

  // Mode switches that change how the socket must be negotiated belong to the machine;
  // only the in-place video re-attach stays here.
  const handleModeChange = (videoSelected: boolean) => {
    if (!videoSelected) {
      send({ type: 'SWITCH_TO_AUDIO' });
    } else if (snapshot.context.socketIsAudioOnly) {
      send({ type: 'REATTEMPT' });
    } else {
      enableVideoInPlace();
    }
  };

  const connectionState = snapshot.value.wsSession;
  const isActive = snapshot.hasTag('sessionActive');
  const { showAudioVisualizer, showConnectionOverlay } = selectVoiceLiveDisplayState({
    connectionState,
    avatarState: peer.avatarState,
    isVideoEnabled: snapshot.context.isVideoEnabled,
    isDegraded,
  });

  return {
    connectionState,
    isActive,
    retryCount: snapshot.context.retryCount,
    handleStart,
    handleStop,
    handleManualRetry,
    avatarState: peer.avatarState,
    videoRef: peer.videoRef,
    isDegraded,
    isOffline,
    isVideoEnabled: snapshot.context.isVideoEnabled,
    handleModeChange,
    showAudioVisualizer,
    showConnectionOverlay,
    errorMessage: snapshot.context.errorMessage,
    inputAnalyser: audio.inputAnalyser,
    outputAnalyser: audio.outputAnalyser,
    devToolsVisible,
    forceAudioFallback,
    setForceAudioFallback,
    forceIceTimeout,
    setForceIceTimeout,
    showTokenUsage,
    setShowTokenUsage,
    tokenUsageSummary: tokenUsage.summary,
  };
};
